/**
 * The reheat plume, and the shock diamonds in it.
 *
 * ## What the diamonds actually are
 *
 * They are not decoration and they are not periodic "flames". An afterburning nozzle is
 * imperfectly expanded: the exhaust leaves at a pressure that does not match the ambient air, so
 * it over-expands, gets squeezed back by an oblique shock, over-compresses, expands again — and
 * repeats, forming a standing train of shock cells down the plume. Each compression re-ignites
 * unburnt fuel and heats the gas, which is why the bright spots are *bright*: they are the hottest
 * parts of the jet, standing still in the air while the gas rushes through them.
 *
 * Two consequences the model here honours, because they are what make it read as real:
 *
 *   - **The cells are stationary relative to the nozzle**, not scrolling. They are a standing
 *     wave. Animating them along the plume is the single most common way to get this wrong.
 *   - **Their spacing grows with pressure ratio** — so they stretch out as the aircraft climbs
 *     into thinner air, and the plume gets longer and more visible with it.
 *
 * ## Why a cone and not particles
 *
 * The plume is a continuous, self-luminous, roughly axisymmetric body of gas. Particles are the
 * wrong primitive for that — you need a great many of them to stop it looking granular, they sort
 * badly against each other, and they cost the whole effects budget. One stretched cone with an
 * analytic radial and longitudinal profile is two triangles' worth of overdraw, is exactly
 * axisymmetric for free, and puts the diamonds where the arithmetic says rather than where a
 * spawn pattern happens to land them.
 *
 * `updateFrame(alpha)`: yes — {@link AfterburnerPlume.updateFrame}, which writes uniforms only.
 */

import { AdditiveBlending, ConeGeometry, Group, Mesh } from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { float, positionLocal, sin, smoothstep, uniform, vec3 } from 'three/tsl';
import { clamp01 } from '../../core/math.js';
import type { NozzleAnchor } from '../airframes.js';

export interface AfterburnerOptions {
  /** Plume length at full reheat [m]. */
  fullLengthM: number;
  /**
   * Where to start from before an exterior has said.
   *
   * Every exterior states its own anchor and {@link AfterburnerPlume.setNozzle} moves the plume
   * there, so this only covers the window between construction and the first exterior — which is
   * no frames in practice, because `main.ts` builds the plume from the built airframe's anchor.
   */
  nozzle: NozzleAnchor;
}

export class AfterburnerPlume {
  readonly group = new Group();

  private readonly mesh: Mesh;
  private readonly geometry: ConeGeometry;
  private readonly material: MeshBasicNodeMaterial;

  private readonly intensity = uniform(0);
  /** Shock-cell spacing as a fraction of the plume length. */
  private readonly cellSpacing = uniform(0.14);
  private readonly coreColour = uniform(vec3(1.0, 0.62, 0.24));
  private readonly options: AfterburnerOptions;
  /** Exit radius of whichever exterior is showing [m]. Drives the mesh's radial scale. */
  private exitRadiusM: number;

  constructor(options: AfterburnerOptions) {
    this.options = options;
    this.exitRadiusM = options.nozzle.exitRadiusM;
    /*
     * A cone, open end at the nozzle, pointing aft.
     *
     * Built with unit height *and unit radius* and scaled in `updateFrame`, so the plume can
     * lengthen, shorten and change nozzle without rebuilding geometry — a plume that only existed
     * at one length would need a new buffer every time the throttle moved, and one that only
     * existed at one radius would need one every time the aeroplane changed.
     */
    this.geometry = new ConeGeometry(1, 1, 18, 1, true);
    this.material = this.buildMaterial();
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = 12;
    // Render space: nose is −Z, so the plume goes aft along +Z. The cone's axis is +Y, so tip it.
    this.mesh.rotation.x = -Math.PI / 2;
    this.group.add(this.mesh);
    this.group.position.copy(options.nozzle.positionM);
    /*
     * Born visible, and hidden by the first {@link updateFrame} — which runs before any frame the
     * player sees.
     *
     * This is not a mistake, it is what gets the plume into `FlightScene.precompile`. A material
     * that is invisible at boot is not compiled at boot, so its pipeline is built the first time
     * it is drawn — measured at 14.9 ms, landing on exactly the frame the pilot slams the throttle
     * into reheat. Compiling it behind the splash with everything else costs nothing, because the
     * intensity uniform is zero and a plume of zero intensity draws nothing.
     */
    this.group.visible = true;
  }

  private buildMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.blending = AdditiveBlending;

    /*
     * `positionLocal.y` runs from −0.5 at the tip to +0.5 at the base, and the cone was built
     * base-at-the-nozzle, so this converts to "fraction of the way down the plume".
     */
    const alongPlume = positionLocal.y.add(0.5).clamp(0, 1);

    // The overall envelope: brightest at the nozzle, gone by the tip.
    const envelope = smoothstep(float(1), float(0.1), alongPlume);

    /*
     * The shock cells. `sin²` of distance along the plume — squared so the bright bands are
     * narrow and the gaps between them wide, which is what the photographs show. Note there is no
     * time term anywhere in this expression: the diamonds stand still.
     */
    const cells = sin(alongPlume.div(this.cellSpacing).mul(Math.PI));
    const diamonds = cells.mul(cells).mul(smoothstep(float(0.9), float(0.05), alongPlume));

    /*
     * Colour: the near field is white-hot and washes out to orange down the plume. The diamonds
     * add on top, which is what makes them the brightest thing in the frame and gives the bloom
     * something to catch.
     */
    const heat = envelope.add(diamonds.mul(0.9));
    const colour = this.coreColour.mul(heat).add(vec3(0.55, 0.72, 1.0).mul(diamonds).mul(0.5));

    material.colorNode = colour.mul(this.intensity);
    material.opacityNode = envelope.mul(this.intensity).mul(0.85);
    return material;
  }

  /**
   * Follow the exterior's nozzle.
   *
   * Called whenever the exterior changes — at boot with the built airframe's anchor and again when
   * the baked glTF arrives, which may be seconds later and may be mid-flight from the aircraft
   * picker. Both the station and the radius move: two exteriors of the same aeroplane are two
   * different pieces of geometry and there is no reason their nozzles should coincide, which is
   * exactly the assumption the hard-coded anchor used to make.
   *
   * Allocation-free, and safe to call every frame even though nothing does: it writes into
   * `group.position` and one number.
   */
  setNozzle(anchor: NozzleAnchor): void {
    this.group.position.copy(anchor.positionM);
    this.exitRadiusM = anchor.exitRadiusM;
  }

  /**
   * @param afterburnerFraction Reheat, 0…1, straight from the flight model.
   * @param ambientPressureRatio Ambient pressure over sea level, 0…1 — the cells stretch as it falls.
   */
  updateFrame(afterburnerFraction: number, ambientPressureRatio: number): void {
    const fraction = clamp01(afterburnerFraction);
    this.intensity.value = fraction;
    this.group.visible = fraction > 0.01;
    if (!this.group.visible) return;

    const length = this.options.fullLengthM * (0.35 + 0.65 * fraction);
    // Radially to the nozzle's exit radius, longitudinally to the plume's length. The geometry is
    // a unit cone, so both are pure scale and neither touches a buffer.
    this.mesh.scale.set(this.exitRadiusM, length, this.exitRadiusM);
    // The cone's origin is its middle, so push it aft by half its length to sit at the nozzle.
    this.mesh.position.set(0, 0, length * 0.5);

    /*
     * Cell spacing grows as the ambient pressure falls: the plume is more badly under-expanded
     * high up, so the shock train stretches and there are fewer, longer cells in the same plume.
     */
    this.cellSpacing.value = 0.11 + 0.16 * (1 - clamp01(ambientPressureRatio));
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
